import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import RefineSearchSidebar from "./components/RefineSearchSidebar";
import ProfileCard from "./components/ProfileCard";
import Pagination from "./components/Pagination";
import axios from "axios";
import config from "../../../config";
import { toast } from "../../Common/Toast";

// Basic but solid email format check
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NewMatches = ({ chatBoxOpen }) => {
  const [activeCarouselIndex, setActiveCarouselIndex] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [inviteSent, setInviteSent] = useState(false);
  const profilesPerPage = 5;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const user = useSelector((state) => state.user.userInfo);
  const lookingFor = user?.looking_for;
  const searchFor = lookingFor === "Bride" ? "Groom" : "Bride";

  const fetchFilteredProfiles = async () => {
    try {
      const response = await axios.get(
        `${config.baseURL}/api/matches/new-matches`,
        {
          params: {
            user_id: user.id,
            looking_for: searchFor,
            ...filters,
          },
        }
      );
      setProfiles(response.data.users || []);
      setCurrentPage(1); // Reset to page 1 on new filter
    } catch (error) {
      console.error("Error fetching profiles", error);
    }
  };

  const handleProfileUpdate = () => {
    setRefreshTrigger((prev) => prev + 1); // Trigger refetch
  };

  useEffect(() => {
    if (searchFor) fetchFilteredProfiles();
  }, [filters, searchFor, refreshTrigger]);

  const handleConnectClick = (id) => {
    setProfiles((prev) =>
      prev.map((profile) =>
        profile.user_id === id
          ? { ...profile, connectionRequest: true, connectionStatus: "pending" }
          : profile
      )
    );
  };

  const handleConnectionCancelled = (id) => {
    setProfiles((prev) =>
      prev.map((profile) =>
        profile.user_id === id
          ? { ...profile, connectionRequest: false, connectionStatus: null }
          : profile
      )
    );
  };

  // Client-side search filter by email
  const searchedProfiles = useMemo(() => {
    if (!searchTerm.trim()) return profiles;
    const term = searchTerm.trim().toLowerCase();
    return profiles.filter((profile) => {
      const email = (profile.email || "").toLowerCase();
      return email.includes(term);
    });
  }, [profiles, searchTerm]);

  // Reset to page 1, and reset invite-sent flag, whenever the search term changes
  useEffect(() => {
    setCurrentPage(1);
    setInviteSent(false);
  }, [searchTerm]);

  // Pagination logic (now applied to the searched/filtered list)
  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = searchedProfiles.slice(
    indexOfFirstProfile,
    indexOfLastProfile
  );

  const trimmedSearch = searchTerm.trim();
  const isSearching = trimmedSearch.length > 0;
  const isSearchAnEmail = EMAIL_REGEX.test(trimmedSearch);
  const showNoUserFound = isSearching && searchedProfiles.length === 0;

  const buildInviteLink = () => {
    const subject = encodeURIComponent(
      `${user?.first_name ? user.first_name + " " : ""}invited you to join!`
    );
    const siteName = config?.siteName || "our matrimony platform";
    const body = encodeURIComponent(
      `Hi,\n\nI think you'd be a great fit on ${siteName}. Join using the link below and complete your profile to start connecting with matches:\n\n${config?.baseURL || ""}\n\nSee you there!`
    );
    const to = encodeURIComponent(trimmedSearch);
    return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;
  };

  const handleInviteViaGmail = async () => {
    try {
      await axios.post(`${config.baseURL}/api/matches/invite`, {
        user_id: user.id,
        invited_email: trimmedSearch,
      });
    } catch (error) {
      console.error("Error recording invite", error);
    } finally {
      window.open(buildInviteLink(), "_blank", "noopener,noreferrer");
      setInviteSent(true);
      toast?.success?.("Invite window opened in Gmail");
    }
  };

  return (
    <div className="p-4">
      <div className="row">
        <div className="col-md-3">
          <RefineSearchSidebar setFilters={setFilters} />
        </div>
        <div className="col-md-9">
          <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
            <h4 className="mb-2 mb-md-0">New Members Who Match Your Preference</h4>
            <div className="position-relative" style={{ maxWidth: "260px", width: "100%" }}>
              <input
                type="text"
                className="form-control"
                style={{ paddingRight: searchTerm ? "30px" : undefined }}
                placeholder="Search by email"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                  className="btn-close position-absolute"
                  style={{
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                    fontSize: "10px",
                    padding: 0,
                  }}
                />
              )}
            </div>
          </div>

          <div className="card border-0 shadow-sm mb-3">
            {currentProfiles.length > 0 ? (
              currentProfiles.map((profile) => (
                <ProfileCard
                  key={profile.id}
                  profile={profile}
                  handleConnectClick={handleConnectClick}
                  handleConnectionCancelled={handleConnectionCancelled}
                  activeIndex={activeCarouselIndex}
                  setActiveIndex={setActiveCarouselIndex}
                  chatBoxOpen={chatBoxOpen}
                  user={user}
                  onProfileUpdate={handleProfileUpdate}
                />
              ))
            ) : showNoUserFound ? (
              <div className="p-5 text-center">
                <div className="text-muted mb-1">No user Found.</div>
                <div className="text-muted mb-3">
                  Enter a complete email address to send an invitation
                </div>
                {isSearchAnEmail && (
                  <button
                    type="button"
                    className="btn text-white"
                    style={{ backgroundColor: "#ff6b6b", border: "none" }}
                    onClick={handleInviteViaGmail}
                  >
                    {inviteSent ? "Invite opened in Gmail ✓" : "Invite via Gmail"}
                  </button>
                )}
              </div>
            ) : (
              <div className="p-4 text-muted">No new matches found.</div>
            )}
          </div>

          {searchedProfiles.length > profilesPerPage && (
            <Pagination
              totalProfiles={searchedProfiles.length}
              profilesPerPage={profilesPerPage}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default NewMatches;